<template>
  <router-view />
  <MaintenanceOverlay />
</template>

<script setup lang="ts">
import { onMounted } from 'vue';
import MaintenanceOverlay from 'src/views/components/common/MaintenanceOverlay.vue';
import { useMaintenancePoller } from '@shared/vue/composables';
import { useAppStore } from 'src/stores/app';
import { config } from 'src/boot/config';

const appStore = useAppStore();

// Phase 5 of the deploy-fence track — SI parity (D6 in the design plan).
// Idle staff tabs flip to the overlay within ~5s of fence-on instead of
// waiting for the next failed request. The poller hits the public,
// fence-aware status endpoint mounted at /v1/maintenance/status (see
// `api/bootstrap/app.php:67`); same endpoint the user-app uses.
const { start } = useMaintenancePoller({
  url: `${config.api.baseUrl}/v1/maintenance/status`,
  onStatus: (status) => {
    if (status.is_active) {
      const errorType =
        status.state === 'fence' ? 'deploy_in_progress' : 'maintenance_window';
      const retryAfter =
        status.fence?.retry_after ??
        status.rule?.retry_after ??
        undefined;
      appStore.setMaintenanceMode(
        true,
        status.fence?.message ?? status.rule?.message ?? undefined,
        retryAfter ?? undefined,
        errorType,
      );
    } else if (appStore.isMaintenanceMode) {
      appStore.clearMaintenanceMode();
    }
  },
  // After 3 consecutive poll failures the API is unreachable (Cloud Run
  // zero-instance or network partition). Show the overlay so staff know
  // something is wrong. The next successful onStatus call corrects the state.
  onNetworkDown: () => {
    appStore.setMaintenanceMode(true, undefined, undefined, 'network_unavailable');
  },
});

onMounted(() => {
  start();
});
</script>
